/**
 * Limits recommendation items for display. Fetches stay unchanged; apply after loading.
 * Empty, null, or non-positive values mean no limit (show all items returned by the API).
 */
export function resolveOptionalPositiveInteger(value: unknown): number | undefined {
  if (value === null || value === undefined || value === '') {
    return undefined;
  }
  const parsed =
    typeof value === 'number'
      ? value
      : typeof value === 'string'
        ? Number.parseInt(value.trim(), 10)
        : Number.NaN;
  if (!Number.isFinite(parsed) || parsed <= 0) {
    return undefined;
  }
  return Math.floor(parsed);
}

export function resolveMaxRecommendations(maxRecommendations: unknown): number | undefined {
  return resolveOptionalPositiveInteger(maxRecommendations);
}

export function resolveMinRecommendations(minRecommendations: unknown): number | undefined {
  return resolveOptionalPositiveInteger(minRecommendations);
}

export function limitRecommendationItems<T>(
  items: readonly T[],
  maxRecommendations: unknown,
): readonly T[] {
  const max = resolveMaxRecommendations(maxRecommendations);
  if (max === undefined) {
    return items;
  }
  return items.slice(0, max);
}

/**
 * Applies optional minimum (raw API count) then maximum (display cap).
 * Returns an empty array when the minimum is configured and not met.
 */
export function recommendationItemsForDisplay<T>(
  items: readonly T[],
  minRecommendations: unknown,
  maxRecommendations: unknown,
): readonly T[] {
  const min = resolveMinRecommendations(minRecommendations);
  if (min !== undefined && items.length < min) {
    return [];
  }
  return limitRecommendationItems(items, maxRecommendations);
}
